import type { QueryClient } from "@tanstack/react-query";
import type { ImageFolderPhoto } from "@shared/api";
import { generateAndUploadThumbnail } from "@/lib/image-thumbnail";
import { queryKeys } from "@/lib/query-keys";

/**
 * After a successful folder photo POST (direct or outbox replay): client thumbnails + query refresh.
 */
export async function runFolderPhotoUploadFollowup(
  qc: QueryClient,
  folderId: string,
  response: unknown,
  files: File[],
): Promise<void> {
  const data = response as { photos?: ImageFolderPhoto[] } | null;
  const photos = data?.photos ?? [];
  if (photos.length === 0 || files.length === 0) {
    await qc.invalidateQueries({ queryKey: ["image-folder", folderId] });
    await qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
    return;
  }

  const photoFileMap = new Map<string, { photo: ImageFolderPhoto; file: File }>();

  photos.forEach((photo) => {
    if (!photo.thumbnail_url) {
      const matchingFile = files.find((f) => {
        const fileNameWithoutExt = f.name.replace(/\.[^/.]+$/, "");
        const photoNameWithoutExt = photo.file_name.replace(/\.[^/.]+$/, "");
        return (
          f.name === photo.file_name ||
          photo.file_name === f.name ||
          fileNameWithoutExt === photoNameWithoutExt ||
          photo.file_name.includes(fileNameWithoutExt) ||
          fileNameWithoutExt.includes(photoNameWithoutExt)
        );
      });
      if (matchingFile) {
        photoFileMap.set(photo.id, { photo, file: matchingFile });
      }
    }
  });

  if (photoFileMap.size === 0) {
    await qc.invalidateQueries({ queryKey: ["image-folder", folderId] });
    await qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
    return;
  }

  await Promise.all(
    Array.from(photoFileMap.entries()).map(async ([photoId, { photo, file }]) => {
      try {
        await generateAndUploadThumbnail(file, folderId, photo.subfolder_type, photoId);
      } catch {
        /* non-fatal */
      }
    }),
  );

  await qc.invalidateQueries({ queryKey: ["image-folder", folderId] });
  await qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
}
